"use client";

import { useState, useEffect, useCallback } from "react";
import Image from "next/image";
import { GalleryImage } from "../types";
import { X, ChevronLeft, ChevronRight, Maximize } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import ScrollAnimation from "./ScrollAnimation";
import { useKlaroConsent } from "@/hooks/useKlaroConsent";

export default function Gallery() {
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [currentIndex, setCurrentIndex] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  
  const { consent, loaded, showModal } = useKlaroConsent("external_media");

  useEffect(() => {
    const checkConsent = () => {
      if (typeof (window as any).klaro !== "undefined") {
        return (window as any).klaro.getManager().getConsent("external_media");
      }
      return true;
    };

    if (consent) {
      setLoading(true);
      fetch("/api.php?action=get_gallery&t=" + Date.now())
        .then((res) => {
          if (!res.ok) throw new Error("Failed to load");
          return res.json();
        })
        .then((data) => {
          setImages(data);
          setLoading(false);
        })
        .catch((err) => {
          console.error("Error loading gallery:", err);
          setLoading(false);
        });
    } else {
      setLoading(false);
    }

    // Polling for consent changes fallback
    const interval = setInterval(() => {
      if (checkConsent() && images.length === 0) {
        clearInterval(interval);
        setLoading(true);
        fetch("/api.php?action=get_gallery&t=" + Date.now())
          .then((res) => res.json())
          .then((data) => {
            setImages(data);
            setLoading(false);
          });
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [consent, images.length]);

  const openModal = (index: number) => setCurrentIndex(index);
  const closeModal = () => setCurrentIndex(null);

  const nextImage = useCallback(() => {
    if (currentIndex === null) return;
    setCurrentIndex((prev) => (prev! + 1) % images.length);
  }, [currentIndex, images.length]);

  const prevImage = useCallback(() => {
    if (currentIndex === null) return;
    setCurrentIndex((prev) => (prev! - 1 + images.length) % images.length);
  }, [currentIndex, images.length]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (currentIndex === null) return;
      if (e.key === "Escape") closeModal();
      if (e.key === "ArrowRight") nextImage();
      if (e.key === "ArrowLeft") prevImage();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentIndex, nextImage, prevImage]);

  // Zeige Platzhalter solange Klaro lädt oder kein Consent da ist
  if (!loaded || !consent) {
    return (
      <section className="section-block gallery-section py-20 px-5 text-center" id="galerie">
        <ScrollAnimation animation="fade-in">
          <div className="bg-gradient-to-br from-primary to-secondary p-10 rounded-2xl border-2 border-accent max-w-2xl mx-auto shadow-2xl">
            <h3 className="text-accent mb-4">Externe Medien blockiert</h3>
            <p className="mb-6">Um die Galerie-Bilder zu sehen, erteile bitte die Einwilligung für &quot;Externe Medien&quot; in den Cookie-Einstellungen.</p>
            <button 
              onClick={showModal}
              className="btn-primary"
            >
              Einstellungen öffnen
            </button>
          </div>
        </ScrollAnimation>
      </section>
    );
  }

  return (
    <section className="section-block gallery-section py-20 px-5" id="galerie">
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent"></div>
        </div>
      ) : (
        <div className="gallery-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 max-w-7xl mx-auto">
          {images.map((image, i) => (
            <ScrollAnimation key={i} animation="fade-in" delay={i * 50} viewportOnce={true}>
              <motion.div 
                whileHover={{ scale: 1.05, translateY: -10 }}
                transition={{ type: "spring", stiffness: 300 }}
                className="gallery-item relative aspect-[4/3] rounded-xl overflow-hidden shadow-xl border-2 border-secondary cursor-pointer group"
                onClick={() => openModal(i)}
              >
                <Image 
                  src={image.thumbnail} 
                  alt={image.alt} 
                  fill 
                  className="object-cover transition-transform duration-500"
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 25vw"
                  unoptimized
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                  <Maximize className="text-accent ml-auto w-6 h-6" />
                </div>
              </motion.div>
            </ScrollAnimation>
          ))}
        </div>
      )}

      <AnimatePresence>
        {currentIndex !== null && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/95 backdrop-blur-md"
          >
            <div className="absolute inset-0 cursor-pointer" onClick={closeModal}></div>
            
            <button 
              onClick={closeModal}
              className="absolute top-10 right-10 text-accent hover:text-white transition-colors z-[10001]"
            >
              <X size={48} />
            </button>

            <button 
              onClick={prevImage}
              className="absolute left-5 md:left-10 top-1/2 -translate-y-1/2 w-12 h-12 md:w-16 md:h-16 flex items-center justify-center bg-primary/80 border-2 border-accent text-accent rounded-full hover:bg-accent hover:text-primary transition-all z-[10001]"
            >
              <ChevronLeft size={32} />
            </button>

            <button 
              onClick={nextImage}
              className="absolute right-5 md:right-10 top-1/2 -translate-y-1/2 w-12 h-12 md:w-16 md:h-16 flex items-center justify-center bg-primary/80 border-2 border-accent text-accent rounded-full hover:bg-accent hover:text-primary transition-all z-[10001]"
            >
              <ChevronRight size={32} />
            </button>

            <motion.div 
              key={currentIndex}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="relative max-w-[90vw] max-h-[85vh] z-[10001]"
            >
              <img 
                src={images[currentIndex].full} 
                alt={images[currentIndex].alt}
                className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
